'use client';

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useMarkovHealth, useMarkovSignal, useMarkovDiagnostics } from '@/hooks/useMarkov';
import { StateMatrix } from '@/components/markov/StateMatrix';
import { RegimeIndicator } from '@/components/markov/RegimeIndicator';
import { Activity, TrendingUp, AlertCircle, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';

export default function MarkovDashboardPage() {
  const { data: health, isLoading: healthLoading } = useMarkovHealth();
  const [selectedStrategy, setSelectedStrategy] = useState<{ instrument: string; timeframe: string } | null>(null);

  const { data: signal } = useMarkovSignal(
    selectedStrategy?.instrument || '',
    selectedStrategy?.timeframe || '',
    !!selectedStrategy
  );

  const { data: diagnostics } = useMarkovDiagnostics(
    selectedStrategy?.instrument || '',
    selectedStrategy?.timeframe || '',
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Markov Trading Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Probabilistic trading strategies with regime detection and confidence intervals
          </p>
        </div>
        <div className="flex gap-3">
          <Link href="/markov/train">
            <Button variant="outline">
              <Activity className="w-4 h-4 mr-2" />
              Train Strategy
            </Button>
          </Link>
          <Link href="/markov/signals">
            <Button>
              <TrendingUp className="w-4 h-4 mr-2" />
              Live Signals
            </Button>
          </Link>
        </div>
      </div>

      {/* System Health */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {health?.status === 'healthy' ? (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            )}
            Markov System Health
          </CardTitle>
          <CardDescription>
            {health?.total_active || 0} active strategies running
          </CardDescription>
        </CardHeader>
        <CardContent>
          {healthLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : health && health.active_strategies.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {health.active_strategies.map((strategy, i) => (
                <Card
                  key={i}
                  className={`cursor-pointer transition-all ${
                    selectedStrategy?.instrument === strategy.instrument &&
                    selectedStrategy?.timeframe === strategy.timeframe
                      ? 'ring-2 ring-blue-600 bg-blue-50'
                      : 'hover:shadow-lg'
                  }`}
                  onClick={() => setSelectedStrategy({
                    instrument: strategy.instrument,
                    timeframe: strategy.timeframe,
                  })}
                >
                  <CardHeader>
                    <CardTitle className="text-lg">{strategy.instrument}</CardTitle>
                    <CardDescription>{strategy.timeframe} timeframe</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">States:</span>
                        <span className="font-semibold">{strategy.num_states}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Last Trained:</span>
                        <span className="font-semibold">
                          {new Date(strategy.last_trained).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No active strategies found</p>
              <Link href="/markov/train">
                <Button>Train Your First Strategy</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Strategy Details */}
      {selectedStrategy && signal && (
        <Tabs defaultValue="signal" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="signal">Current Signal</TabsTrigger>
            <TabsTrigger value="matrix">Transition Matrix</TabsTrigger>
            <TabsTrigger value="regime">Regime Analysis</TabsTrigger>
          </TabsList>

          {/* Current Signal Tab */}
          <TabsContent value="signal" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Current Signal - {selectedStrategy.instrument}</CardTitle>
                <CardDescription>
                  Real-time probabilistic trading signal with expected value bounds
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <div className="text-sm text-muted-foreground">Action</div>
                    <div className={`text-2xl font-bold uppercase ${
                      signal.action === 'buy' ? 'text-green-600' :
                      signal.action === 'sell' ? 'text-red-600' :
                      'text-gray-600'
                    }`}>
                      {signal.action}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Contracts</div>
                    <div className="text-2xl font-bold">{signal.contracts}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Confidence</div>
                    <div className="text-2xl font-bold">{(signal.confidence * 100).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Win Probability</div>
                    <div className="text-2xl font-bold">{(signal.win_probability * 100).toFixed(1)}%</div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm font-semibold mb-2">Expected Value Bounds</div>
                    <div className="relative h-16 bg-gradient-to-r from-red-100 via-yellow-100 to-green-100 rounded-lg">
                      <div className="absolute inset-0 flex items-center justify-between px-4 text-sm font-semibold">
                        <span className="text-red-700">Floor: {signal.ev_bounds.floor.toFixed(4)}</span>
                        <span className="text-blue-700">EV: {signal.ev_bounds.mean.toFixed(4)}</span>
                        <span className="text-green-700">Ceiling: {signal.ev_bounds.ceiling.toFixed(4)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Current State</div>
                      <div className="text-lg font-bold">State {signal.current_state}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Predicted State</div>
                      <div className="text-lg font-bold">State {signal.predicted_state}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-1">AI Reasoning</div>
                    <div className="text-sm bg-gray-50 p-3 rounded-md">
                      {signal.reasoning}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transition Matrix Tab */}
          <TabsContent value="matrix">
            <Card>
              <CardHeader>
                <CardTitle>State Transition Matrix</CardTitle>
                <CardDescription>
                  Probability of transitioning between states
                </CardDescription>
              </CardHeader>
              <CardContent className="flex justify-center">
                {diagnostics?.transition_matrix ? (
                  <StateMatrix matrix={diagnostics.transition_matrix} />
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No transition matrix data available
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Regime Analysis Tab */}
          <TabsContent value="regime">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <RegimeIndicator regime={signal.regime} />

              <Card>
                <CardHeader>
                  <CardTitle>Regime Trading Guidelines</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <div className="font-semibold text-blue-900 mb-1">Current Regime</div>
                    <p className="text-sm text-blue-700">
                      {signal.regime.regime} ({(signal.regime.confidence * 100).toFixed(1)}% confidence)
                    </p>
                  </div>

                  <div>
                    <div className="text-sm font-semibold mb-2">Key Characteristics</div>
                    <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                      {signal.regime.characteristics.map((char, i) => (
                        <li key={i}>{char}</li>
                      ))}
                    </ul>
                  </div>

                  <div className={`p-3 rounded-md ${
                    signal.confidence > 0.7 ? 'bg-green-50 border border-green-200' :
                    signal.confidence > 0.4 ? 'bg-yellow-50 border border-yellow-200' :
                    'bg-red-50 border border-red-200'
                  }`}>
                    <div className="font-semibold mb-1">
                      {signal.confidence > 0.7 ? 'High Confidence Signal' :
                       signal.confidence > 0.4 ? 'Medium Confidence Signal' :
                       'Low Confidence Signal'}
                    </div>
                    <p className="text-sm">
                      {signal.confidence > 0.7 ? 'Strong signal in favorable regime - consider acting' :
                       signal.confidence > 0.4 ? 'Moderate signal - proceed with caution' :
                       'Weak signal - consider waiting for better setup'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {!selectedStrategy && health && health.active_strategies.length > 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Activity className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg text-muted-foreground">
              Select a strategy above to view details
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
